import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, BookOpen, Share2, Settings2 } from "lucide-react";
import { CAVEATS, WHAT_TO_DO, BIBLIOGRAPHY, ALERT, MISMATCH, COUNT, BEHAVIOR, mergeGuideSections } from "./spikingData";
import { renderInline } from "./renderInline";
import SymptomChecker from "./SymptomChecker";
import { useAuth } from "../../App";
import { useNorthStarGuide } from "../../hooks/useNorthStar";
import { shareSafetyCard } from "../../utils/safetyShareCard";
import NorthStarGuideEditor from "./NorthStarGuideEditor";
import { GuideSection } from "./GuideSection";
import { StarPathMark } from "../StarPathMark";
const STARFLOWER = "https://customer-assets.emergentagent.com/job_211279cf-e69f-4285-b28c-6209ba7e3f92/artifacts/mi6oafuj_starflower-star-of-destiny-logo-png_orig%202.png";

const BLUE = "#1D4ED8";
const H3 = ({ children, testId }) => <h3 className="text-base md:text-xl font-extrabold uppercase tracking-[0.15em] text-[#1D4ED8]" data-testid={testId}>{children}</h3>;

const Quotes = ({ items }) => (
  <div className="pl-5 border-l-2 border-[#1D4ED8]/30 space-y-1.5 italic text-[#5C5C5C] text-[15px]">
    {items.map((q, i) => <p key={i}>“{q}”</p>)}
  </div>
);

// North Star guide — one consistent, unmissable card under every landing/splash.
export const NorthStarGuideBlock = ({ block, layoutKey = "", containerClass }) => {
  const cfg = block.config || {};
  const compact = cfg.compact !== false;
  const hideShare = cfg.hide_share !== false;
  const { pathname } = useLocation();
  const { user } = useAuth();
  const saved = useNorthStarGuide();
  const sections = mergeGuideSections(saved);
  const guideRoute = cfg.guide_route || "/abuse#signs";
  const onGuidePage = pathname === guideRoute.split("#")[0];
  const [open, setOpen] = useState(Boolean(cfg.open_by_default) || (typeof window !== "undefined" && window.location.hash === "#north-star"));
  const [bib, setBib] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const showSource = (n) => {
    setBib(true);
    setTimeout(() => document.getElementById(`north-star-src-${n}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };
  const share = async () => {
    setSharing(true);
    try {
      const r = await shareSafetyCard();
      if (r.method === "native") toast.success("Shared — thanks for spreading the signs");
      else if (r.method === "download") toast.success("Card saved — post it wherever your people are");
    } catch { toast.error("Couldn't build the card — try again"); }
    setSharing(false);
  };
  const contained = !layoutKey.startsWith("onscreen:");

  const BODY = {
    checker: (s) => (
      <div>
        <SymptomChecker onSourceClick={showSource} />
        <p className="mt-3 text-base text-[#5C5C5C]" data-testid="north-star-symptoms-note">Spiking usually looks like being far drunker than you should be — the tell is <em>how fast</em> it hit versus <em>how much</em> you drank.</p>
      </div>
    ),
    what_to_do: (s) => (
      <div>
        <h3 className="text-xl md:text-2xl font-extrabold text-[#1D4ED8]">{s.title}</h3>
        <ol className="mt-3 space-y-3" data-testid="north-star-what-to-do">
          {WHAT_TO_DO.map((t, i) => (
            <li key={i} className="flex items-start gap-4 rounded-xl bg-white border border-[#E2E0DC] px-4 py-3" data-testid={`north-star-step-${i + 1}`}>
              <span className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold leading-none text-[#1D4ED8] shrink-0 w-8 text-center" aria-hidden>{i + 1}</span>
              <span className="text-base md:text-lg leading-relaxed text-[#1A1A1A] pt-0.5">{renderInline(t, BLUE)}</span>
            </li>
          ))}
        </ol>
      </div>
    ),
    alert: (s) => (
      <div className="rounded-xl border-l-4 border-[#B91C1C] bg-[#FEF2F2] px-5 py-4" data-testid="north-star-alert">
        <h3 className="text-sm md:text-base font-extrabold uppercase tracking-[0.15em] text-[#B91C1C]">{s.title}</h3>
        <p className="mt-2 text-[15px] leading-relaxed text-[#1A1A1A] [&_strong]:text-[#B91C1C]">{renderInline(ALERT.body, BLUE)}</p>
        <p className="mt-2 text-xs text-[#5C5C5C]">Sources: {ALERT.sources.map((x, i) => <a key={i} href={x.url} target="_blank" rel="noopener noreferrer" className="underline text-[#B91C1C] mr-2">{x.label}</a>)}</p>
      </div>
    ),
    mismatch: (s) => (
      <div data-testid="north-star-mismatch">
        <H3>{s.title}</H3>
        <p className="mt-3 text-[15px] text-[#3a3a3a]">{MISMATCH.intro}</p>
        <div className="mt-3"><Quotes items={MISMATCH.quotes} /></div>
        <p className="mt-3 text-[15px] leading-relaxed text-[#1A1A1A] [&_strong]:text-[#1A1A1A]">{renderInline(MISMATCH.body, BLUE)}</p>
      </div>
    ),
    count: (s) => (
      <div data-testid="north-star-count">
        <H3>{s.title}</H3>
        <div className="mt-3 flex flex-col sm:flex-row gap-4 sm:items-start">
          <div className="font-['Playfair_Display'] text-5xl md:text-6xl font-bold text-[#1D4ED8] leading-none shrink-0">{COUNT.big}</div>
          <div className="space-y-3 text-[15px] leading-relaxed text-[#3a3a3a] [&_strong]:text-[#1A1A1A]">
            <p>{renderInline(COUNT.body, BLUE)}</p>
            <p>{renderInline(COUNT.stat, BLUE)}</p>
          </div>
        </div>
      </div>
    ),
    behavior: (s) => (
      <div data-testid="north-star-behavior">
        <H3>{s.title}</H3>
        <p className="mt-3 text-[15px] leading-relaxed text-[#3a3a3a] [&_strong]:text-[#1A1A1A]">{renderInline(BEHAVIOR.body, BLUE)}</p>
        <p className="mt-3 font-['Playfair_Display'] text-xl md:text-2xl italic text-[#1A1A1A]">{BEHAVIOR.close}</p>
      </div>
    ),
    caveats: (s) => (
      <div>
        <H3 testId="north-star-panic-heading">{s.title}</H3>
        <ul className="mt-3 space-y-2 text-base text-[#3a3a3a] list-disc pl-5">{CAVEATS.map((t, i) => <li key={i}>{t}</li>)}</ul>
      </div>
    ),
    sources: (s) => (
      <div>
        <button type="button" onClick={() => setBib(!bib)} aria-expanded={bib} className="inline-flex items-center gap-2 rounded-full bg-white text-[#1D4ED8] border-2 border-[#1D4ED8]/30 px-5 py-2.5 text-sm font-extrabold hover:border-[#1D4ED8]" data-testid="north-star-bibliography-toggle">
          <BookOpen className="h-4 w-4" /> {s.title} <ChevronDown className={`h-4 w-4 transition-transform ${bib ? "rotate-180" : ""}`} />
        </button>
        {bib && (
          <ol className="mt-3 list-decimal pl-5 text-sm text-[#3a3a3a] space-y-1.5" data-testid="north-star-bibliography">
            {BIBLIOGRAPHY.map((x, i) => <li key={x.url} id={`north-star-src-${i + 1}`} className="scroll-mt-24 target:bg-[#EEF3FF] rounded px-1"><a href={x.url} target="_blank" rel="noopener noreferrer" className="underline text-[#1D4ED8]">{x.label}</a></li>)}
          </ol>
        )}
      </div>
    ),
  };

  return (
    <div className={contained ? (containerClass || "max-w-7xl mx-auto px-6 md:px-12 py-6") : undefined}>
    <section id="north-star" className="scroll-mt-24 rounded-2xl border-2 border-[#1D4ED8]/30 bg-white shadow-[0_18px_50px_-24px_rgba(29,78,216,0.45)] overflow-hidden" data-testid="north-star-guide">
      <div className="px-5 sm:px-8 py-6 sm:py-7">
        {/* compact: title row with the buttons beside it, no lede (compact); hideShare drops the share button */}
        <div className={compact ? "flex flex-col md:flex-row md:items-center justify-between gap-x-6 gap-y-3" : ""}>
          <div className={compact ? "min-w-0 flex-1" : ""}>
            <div className="flex items-start gap-2.5 text-sm sm:text-base md:text-lg font-extrabold uppercase tracking-[0.18em] leading-snug text-[#1D4ED8]" data-testid="north-star-eyebrow"><img src={STARFLOWER} alt="Stars 4 Peace" className="h-7 w-7 md:h-8 md:w-8 shrink-0 rounded-full object-cover ring-2 ring-[#1D4ED8]/30 -mt-0.5" data-testid="north-star-starflower" /> <span>{cfg.eyebrow || "Drink & Food Spiking — Protecting People Professionally"}</span></div>
            <h2 className={`${compact ? "mt-1.5 text-xl sm:text-2xl md:text-3xl" : "mt-3 text-2xl sm:text-3xl md:text-4xl"} font-['Playfair_Display'] font-bold leading-tight text-[#1A1A1A]`}>{cfg.title || "Have a REAL good time."}</h2>
          <p className={compact ? `mt-1.5 text-sm md:text-[15px] leading-relaxed text-[#3a3a3a] max-w-3xl [&_strong]:text-[#1A1A1A] ${open ? "" : "line-clamp-1"}` : "mt-3 text-base md:text-lg leading-relaxed text-[#3a3a3a] max-w-3xl [&_strong]:text-[#1A1A1A]"} data-testid="north-star-lede">{renderInline(cfg.lede || "Desire is natural — **drugs that induce desire violate your consent.** Protect yourself & friends: **know the signs** of food & drink spiking & get to **know the symptoms** of the drugs used **in case something happens**. Eg: [New & deadly: Cychlorphine —](https://www.king5.com/article/news/nation-world/what-is-cychlorphine-synthetic-opioid-linked-overdose-deaths/507-f1936135-8e5a-408e-bd0d-96f56a74aa5f) [KING 5 NEWS](https://www.king5.com/article/news/nation-world/what-is-cychlorphine-synthetic-opioid-linked-overdose-deaths/507-f1936135-8e5a-408e-bd0d-96f56a74aa5f)", BLUE)}</p>
          </div>
        {/* compact: the buttons stack in a column on the right so the card stays one row tall on desktop */}
        <div className={`${compact ? "min-w-0 max-w-full md:shrink-0 md:flex-col md:items-stretch md:gap-2 [&>*]:md:justify-center" : "mt-5"} flex flex-wrap items-center gap-3`} data-testid="north-star-actions">
          <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
            className={`inline-flex items-center gap-2 rounded-full bg-white text-[#1D4ED8] border-2 border-[#1D4ED8]/30 ${compact ? "px-5 py-2.5 text-sm md:text-base" : "px-7 py-3.5 text-base md:text-lg"} font-extrabold shadow-[0_12px_32px_-10px_rgba(29,78,216,0.45)] transition-transform hover:-translate-y-0.5 hover:border-[#1D4ED8]`}
            data-testid="north-star-more">
            <StarPathMark className="h-5 w-5 ring-[#1D4ED8]/30" /> {open ? "LESS" : (cfg.more_label || "MORE")} <ChevronDown className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          <Link to={cfg.abuse_route || "/abuse#the-group"} className={`inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-white ${compact ? "px-4 py-2.5 text-sm" : "px-5 py-3 text-sm md:text-base"} font-extrabold hover:bg-black transition-colors no-underline`} data-testid="north-star-abuse-link">
            <StarPathMark className="h-5 w-5" /> {cfg.abuse_label || "In a harmful crowd? Know this:"} →
          </Link>
          <Link to={cfg.resources_route || "/resources"} className={`inline-flex items-center gap-2 rounded-full bg-white text-[#1A1A1A] border-2 border-[#1A1A1A]/20 ${compact ? "px-4 py-2.5 text-sm" : "px-5 py-3 text-sm md:text-base"} font-extrabold hover:border-[#1A1A1A] transition-colors no-underline`} data-testid="north-star-resources-link">
            <StarPathMark className="h-5 w-5 ring-[#1A1A1A]/20" /> {cfg.resources_label || "Find more resources"}
          </Link>
          {!hideShare && (
            <button type="button" onClick={share} disabled={sharing} className="inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] text-white px-5 py-3 text-sm md:text-base font-extrabold hover:bg-[#1e40af] disabled:opacity-60" data-testid="north-star-share">
              <Share2 className="h-4 w-4" /> {sharing ? "Building card…" : "Share this card"}
            </button>
          )}
          {user?.role === "admin" && (
            <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 rounded-full border border-amber-400 bg-amber-50 text-amber-900 px-3 py-1.5 text-xs font-bold hover:bg-amber-100" data-testid="north-star-arrange">
              <Settings2 className="h-3.5 w-3.5" /> Arrange guide
            </button>
          )}
        </div>
        </div>
      </div>
      {open && (
        <div className="border-t border-[#E2E0DC] bg-[#F7F9FF] px-5 sm:px-8 py-7 space-y-8" data-testid="north-star-body">
          {hideShare && (
            <div className="flex flex-wrap items-center gap-3 -mb-2">
              <button type="button" onClick={share} disabled={sharing} className="inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] text-white px-5 py-2.5 text-sm font-extrabold hover:bg-[#1e40af] disabled:opacity-60" data-testid="north-star-share-expanded">
                <Share2 className="h-4 w-4" /> {sharing ? "Building card…" : (cfg.share_label || "Share — send to a friend")}
              </button>
              <span className="text-xs text-[#5C5C5C]">Makes a shareable card of this guide.</span>
            </div>
          )}
          {sections.filter((s) => s.enabled).map((s) => (
            <GuideSection key={s.key} section={s}>{BODY[s.key](s)}</GuideSection>
          ))}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            {!onGuidePage && <Link to={guideRoute} className="inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] text-white px-6 py-3 text-sm md:text-base font-extrabold hover:bg-[#1e40af]" data-testid="north-star-guide-link"><StarPathMark className="h-5 w-5" /> {cfg.guide_label || "RECOGNIZE ABUSE TACTICS — Understanding Abuse →"}</Link>}
            <Link to="/disclaimer" className="text-xs underline text-[#5C5C5C] hover:text-[#1D4ED8]" data-testid="north-star-disclaimer-link">Educational only — does not diagnose. Read our Medical & Safety Disclaimer.</Link>
          </div>
        </div>
      )}
      {editing && <NorthStarGuideEditor sections={sections} onClose={() => setEditing(false)} />}
    </section>
    </div>
  );
};

export default NorthStarGuideBlock;
